import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PushPin, Trash } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    getAccessToken,
    addToCallPlan,
    updateCallPlan,
    removeFromCallPlan,
} from "@/lib/api";
import { CALL_PLAN_RESULTS } from "@/lib/callPlan";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from "@/components/ui/alert-dialog";

const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

export const CallPlanBtn = ({ property, className = "", size = "w-7 h-7" }) => {
    const nav = useNavigate();
    const initial = Boolean(property.isInCallPlan ?? property.isCallPlan);
    const [added, setAdded] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [loginPrompt, setLoginPrompt] = useState(false);

    useEffect(() => {
        setAdded(Boolean(property.isInCallPlan ?? property.isCallPlan));
    }, [property.isInCallPlan, property.isCallPlan]);

    const onClick = async (e) => {
        stop(e);

        if (!getAccessToken()) {
            setLoginPrompt(true);
            return;
        }

        if (busy) return;

        setBusy(true);
        try {
            if (added) {
                await removeFromCallPlan(property.idProperty);
                setAdded(false);
                toast.success("Zəng planından çıxarıldı");
            } else {
                await addToCallPlan(property.idProperty);
                setAdded(true);
                toast.success("Zəng planına əlavə olundu");
            }
        } catch {
            toast.error("Əməliyyat mümkün olmadı");
        } finally {
            setBusy(false);
        }
    };

    const confirmLogin = () => {
        setLoginPrompt(false);
        nav("/login");
    };

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                onMouseDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                disabled={busy}
                data-testid={`call-plan-btn-${property.idProperty}`}
                title={added ? "Zəng planından çıxar" : "Zəng planına əlavə et"}
                aria-label="Zəng planı"
                className={`${size} shrink-0 rounded-full flex items-center justify-center shadow-md transition disabled:opacity-60 ${added
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-white text-slate-700 hover:bg-slate-100"
                    } ${className}`}
            >
                <PushPin size={14} weight={added ? "fill" : "bold"} />
            </button>

            <AlertDialog open={loginPrompt} onOpenChange={setLoginPrompt}>
                <AlertDialogContent
                    data-testid="call-plan-login-dialog"
                    onClick={(e) => e.stopPropagation()}
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle>Giriş tələb olunur</AlertDialogTitle>
                        <AlertDialogDescription>
                            Zəng planına əlavə etmək üçün daxil olun.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="call-plan-login-cancel">
                            Ləğv et
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="call-plan-login-confirm"
                            onClick={confirmLogin}
                        >
                            Daxil ol
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export const CallPlanStatus = ({ property, compact = false }) => {
    const qc = useQueryClient();
    const [value, setValue] = useState(property.callPlan?.resultTypeId ?? 1);
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        setValue(property.callPlan?.resultTypeId ?? 1);
    }, [property.callPlan?.resultTypeId]);

    const current = CALL_PLAN_RESULTS.find((r) => r.id === value);

    const onChange = async (v) => {
        const next = Number(v);
        const prev = value;
        setValue(next);

        try {
            await updateCallPlan(property.idProperty, { resultTypeId: next });
            toast.success("Status yeniləndi");
            qc.invalidateQueries({ queryKey: ["listing"] });
        } catch {
            setValue(prev);
            toast.error("Statusu dəyişmək mümkün olmadı");
        }
    };

    const onDelete = async () => {
        try {
            await removeFromCallPlan(property.idProperty);
            toast.success("Zəng planından silindi");
            qc.invalidateQueries({ queryKey: ["listing"] });
        } catch {
            toast.error("Silmək mümkün olmadı");
        }
        setConfirmOpen(false);
    };

    return (
        <div
            className={`${compact ? "inline-flex" : "flex w-full"} items-center gap-1.5`}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <Select value={String(value)} onValueChange={onChange}>
                <SelectTrigger
                    data-testid={`call-plan-status-${property.idProperty}`}
                    className={`h-8 ${compact ? "w-[190px]" : "flex-1"} rounded-lg text-xs font-semibold border ${current?.cls || ""}`}
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {CALL_PLAN_RESULTS.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>
                            {r.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <button
                type="button"
                onClick={(e) => {
                    stop(e);
                    setConfirmOpen(true);
                }}
                data-testid={`call-plan-remove-${property.idProperty}`}
                title="Plandan sil"
                aria-label="Plandan sil"
                className="w-8 h-8 shrink-0 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive hover:bg-destructive/5 transition-colors"
            >
                <Trash size={14} />
            </button>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="call-plan-remove-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Zəng planından silinsin?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu elan zəng planınızdan çıxarılacaq.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Xeyr</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete}>Sil</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};